import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "@/types/store";
import styles from "./style.module.css";
import * as tagsReducer from "@/containers/Tags/reducer";
import * as chatGPTReducer from "@/containers/ChatGPT/reducer";
import * as contentGenerationReducer from "@/containers/ContentGeneration/reducer";
import { Button } from "@/aible";
import Select, { components } from "react-select";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import * as aibleAnywhereReducer from "@/containers/AibleAnywhere/reducer";
import * as nextReducer from "@/containers/Next/NextReducer";
import * as accountsReducer from "@/containers/Accounts/reducer";
import { ClawTaskItem } from "components/Templates/AgentC";
import { LineInput } from "components/Inputs/LineInput";
import { SkillsOverview } from "@/components/AibleAgent/AibleAgent";
import { CronJobs, APIInvocations } from "components/Templates/AgentC";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/aible/Tooltip/Tooltip";
import { selectPlugins, sendOpenClawMessage } from "@/containers/OpenClaw";
function stringifyPromptText(value) {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value === "string") {
        return value;
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return undefined;
    }
}
function getMessagePromptText(message) {
    return stringifyPromptText(message?.userText ?? message?.text);
}
function getFallbackOriginalPrompt(conversationArray, messageIndex) {
    try {
        for (let i = messageIndex - 1; i >= 0; i--) {
            const message = conversationArray?.[i];
            if (message?.user === "User") {
                return getMessagePromptText(message);
            }
        }
    }
    catch {
        return undefined;
    }
    return undefined;
}
function getSubmittedReportPrompt(toolCall, conversationArray, messageIndex) {
    try {
        return (stringifyPromptText(toolCall?.input?.query) ??
            stringifyPromptText(toolCall?.input?.question) ??
            stringifyPromptText(toolCall?.input?.prompt) ??
            getFallbackOriginalPrompt(conversationArray, messageIndex));
    }
    catch {
        return getFallbackOriginalPrompt(conversationArray, messageIndex);
    }
}
const SandboxWorkList = ({}) => {
    const dispatch = useDispatch();
    const conversationArray = useSelector((state) => chatGPTReducer.getConversationArray(state, undefined));
    const nextId = useSelector((state) => nextReducer.getCurrentNextId(state)) ?? "new";
    const reportLabel = useSelector((state) => nextReducer.getReportLabel(state, nextId));
    const accountId = useSelector((state) => accountsReducer.getSelectedAccountId(state));
    const useGuardrails = useSelector((state) => Boolean(chatGPTReducer.getGuardrails(state)));
    const useCache = useSelector((state) => chatGPTReducer.getMaxCacheAge(state) !== undefined);
    const [isCreatingClawAgent, updateIsCreatingClawAgent] = useState(false);
    const [showClawSettings, updateShowClawSettings] = useState(false);
    const [hasAttemptedFetch, updateHasAttemptedFetch] = useState({});
    const aibleAnywhereProjects = useSelector((state) => aibleAnywhereReducer.getBase(state));
    // Initial claw agent for cron job setup
    const initialClawAgent = {
        isAgentC: true,
        isEnabled: false,
        accountId: accountId,
        name: reportLabel,
        cronJobs: [],
        apiInvocations: [],
        workflowOrigin: "OPENSHELL",
        isAssistant: true,
        tags: ["AIBLE_SAFE_CLAW"],
        description: "Aible Assistant generated from OpenShell"
    };
    const [clawAgent, updateClawAgent] = useState(initialClawAgent);
    const submittedWorkflowItems = (conversationArray ?? []).reduce((ret, m, index) => {
        const messageToolCalls = m?.toolCalls;
        if (!messageToolCalls) {
            return ret;
        }
        const validSubmittedReportCall = messageToolCalls?.find((toolCall) => (toolCall?.name === "aible_submit_final_report" || toolCall?.name === "aible_record_skill_workflow") &&
            toolCall?.responseId !== undefined);
        if (validSubmittedReportCall?.responseId === undefined) {
            return ret;
        }
        ret.push({
            executionId: Number(validSubmittedReportCall.responseId),
            originalPrompt: getSubmittedReportPrompt(validSubmittedReportCall, conversationArray, index)
        });
        return ret;
    }, []);
    const submittedReportExecutionIds = submittedWorkflowItems.map((item) => item.executionId);
    const submittedReportPromptByExecutionId = submittedWorkflowItems.reduce((ret, item) => {
        ret[item.executionId] = item.originalPrompt;
        return ret;
    }, {});
    const executionWorkflow = useSelector((state) => contentGenerationReducer.getExecutionWorkflow(state));
    const isFetchingExecutionWorkflow = useSelector((state) => contentGenerationReducer.getIsFetchingExecutionWorkflow(state));
    useEffect(() => {
        submittedReportExecutionIds.forEach((executionId) => {
            if (executionWorkflow?.[executionId] === undefined &&
                isFetchingExecutionWorkflow?.[executionId] !== true &&
                !hasAttemptedFetch?.[executionId]) {
                updateHasAttemptedFetch({ ...hasAttemptedFetch, [executionId]: true });
                dispatch(contentGenerationReducer.fetchExecutionWorkflowPlan(executionId, true));
            }
        });
    }, [submittedReportExecutionIds?.length, hasAttemptedFetch]);
    const [workItemSettings, updateWorkItemSettings] = useState({});
    const workFlowOptions = submittedReportExecutionIds.map((o) => {
        const workflowLabel = workItemSettings?.[o]?.name;
        return {
            value: o,
            label: workflowLabel
        };
    });
    const handleSaveWorkflow = async (placeHolderAgent, executionId, itemSettings, saveToMyWork) => {
        try {
            const retOptions = {
                ...itemSettings,
                saveToMyWork: saveToMyWork ?? false
            };
            const newAgent = await dispatch(aibleAnywhereReducer.saveGeneratedWorkflow(placeHolderAgent, undefined, retOptions));
            return newAgent;
        }
        catch {
            console.log("Error Saving Workflow:", executionId);
            Promise.resolve();
        }
    };
    // Find the workflowOptions and add the execution_steps of those placeholderAgents and add them to a claw agent and create it.
    const handleCreateClawAgent = async () => {
        updateIsCreatingClawAgent(true);
        let newClawAgent = {
            ...clawAgent
        };
        const cronJobs = newClawAgent?.cronJobs;
        const apiInvocations = newClawAgent?.apiInvocations;
        const requiredWorkflows = [];
        (cronJobs ?? []).forEach((job) => {
            const workflowOptions = job?.workflowOptions;
            workflowOptions.forEach((o) => {
                if (!requiredWorkflows.includes(o)) {
                    requiredWorkflows.push(o);
                }
            });
        });
        (apiInvocations ?? []).forEach((invocation) => {
            const workflowOptions = invocation?.workflowOptions;
            workflowOptions.forEach((o) => {
                if (!requiredWorkflows.includes(o)) {
                    requiredWorkflows.push(o);
                }
            });
        });
        const executionWorkflows = {};
        for (const executionId of requiredWorkflows) {
            const placeholderAnywhereId = `new_${executionId}`;
            const placeholderAgent = aibleAnywhereProjects?.[placeholderAnywhereId];
            if (!placeholderAgent) {
                console.log("Error finding placeholder workflow agent");
            }
            const placeholderAgentWorkflowData = {
                name: placeholderAgent?.name,
                description: placeholderAgent?.description,
                originalPrompt: placeholderAgent?.originalPrompt,
                execution_steps: placeholderAgent?.execution_steps,
                runToggle: "BACKEND",
                workflowOrigin: "OPENSHELL"
            };
            const newWorkflowData = {
                ...placeholderAgentWorkflowData,
                ...(workItemSettings?.[executionId] ?? {})
            };
            (cronJobs ?? []).forEach((job, index) => {
                if ((job?.workflowOptions ?? []).includes(executionId)) {
                    if (executionWorkflows?.[executionId] === undefined) {
                        executionWorkflows[executionId] = newWorkflowData;
                    }
                    const newAgentBlueprintTool = {
                        id: 9,
                        description: newWorkflowData?.description ?? "",
                        execution_id: executionId,
                        label: newWorkflowData?.name ?? "Generated Workflow from OpenShell",
                        value: "step_by_step_agent_tool",
                        workflow_plan: executionId
                    };
                    const blueprintOptionsUpdate = [...(job?.blueprintOptions ?? []), newAgentBlueprintTool];
                    const jobUpdate = {
                        ...job,
                        blueprintOptions: blueprintOptionsUpdate
                    };
                    const updatedCronJobs = newClawAgent.cronJobs?.map((j, i) => (i === index ? jobUpdate : j));
                    newClawAgent = {
                        ...newClawAgent,
                        cronJobs: updatedCronJobs
                    };
                }
            });
            (apiInvocations ?? []).forEach((apiInvocation, index) => {
                if ((apiInvocation?.workflowOptions ?? []).includes(executionId)) {
                    if (executionWorkflows?.[executionId] === undefined) {
                        executionWorkflows[executionId] = newWorkflowData;
                    }
                    const newAgentBlueprintTool = {
                        id: 9,
                        description: newWorkflowData?.description ?? "",
                        execution_id: executionId,
                        label: newWorkflowData?.name ?? "Generated Workflow from OpenShell",
                        value: "step_by_step_agent_tool",
                        workflow_plan: executionId
                    };
                    const blueprintOptionsUpdate = [...(apiInvocation?.blueprintOptions ?? []), newAgentBlueprintTool];
                    const apiInvocationUpdate = {
                        ...apiInvocation,
                        blueprintOptions: blueprintOptionsUpdate
                    };
                    const updatedApiInvocation = newClawAgent.apiInvocations?.map((j, i) => i === index ? apiInvocationUpdate : j);
                    newClawAgent = {
                        ...newClawAgent,
                        apiInvocations: updatedApiInvocation
                    };
                }
            });
        }
        newClawAgent = {
            ...newClawAgent,
            executionWorkflows: { ...executionWorkflows }
        };
        (newClawAgent?.cronJobs ?? []).forEach((job, index) => {
            if (job?.workflowOptions) {
                delete newClawAgent.cronJobs[index].workflowOptions;
            }
        });
        (newClawAgent?.apiInvocations ?? []).forEach((apiInvocation, index) => {
            if (apiInvocation?.workflowOptions) {
                delete newClawAgent.apiInvocations[index].workflowOptions;
            }
        });
        try {
            await dispatch(aibleAnywhereReducer.saveClawAgent(newClawAgent, newClawAgent?.name, undefined, false, true, "CHATAIBLE_AGENT_C"));
        }
        catch {
            console.log("Error saving overall assistant agent");
        }
        finally {
            updateIsCreatingClawAgent(false);
        }
    };
    const updateItemSettings = (executionId, val) => {
        const itemSettingsUpdate = {
            ...workItemSettings,
            [executionId]: val
        };
        updateWorkItemSettings(itemSettingsUpdate);
    };
    useEffect(() => {
        const itemSettingsUpdate = { ...workItemSettings };
        for (const [index, executionId] of submittedReportExecutionIds.entries()) {
            if (workItemSettings?.[executionId] === undefined) {
                const executionWorkflowData = executionWorkflow?.[executionId];
                const originalPrompt = submittedReportPromptByExecutionId?.[executionId];
                const defaultItemSettings = {
                    name: `Workflow ${index + 1}`,
                    description: executionWorkflowData?.data?.description ?? "Aible Workflow generated by OpenShell",
                    originalPrompt,
                    tags: ["AIBLE_SAFE_CLAW"]
                };
                itemSettingsUpdate[executionId] = defaultItemSettings;
            }
            else if (workItemSettings?.[executionId]?.originalPrompt === undefined &&
                submittedReportPromptByExecutionId?.[executionId] !== undefined) {
                itemSettingsUpdate[executionId] = {
                    ...workItemSettings[executionId],
                    originalPrompt: submittedReportPromptByExecutionId[executionId]
                };
            }
        }
        updateWorkItemSettings(itemSettingsUpdate);
    }, [submittedReportExecutionIds?.length]);
    const conversationArrayLength = useSelector((state) => chatGPTReducer.getConversationArrayLength(state));
    const sandboxPlugins = useSelector(selectPlugins);
    const sandboxAibleSkillEnabled = sandboxPlugins?.entries?.["aible-plugin"]?.enabled;
    const isAskingAgent = useSelector((state) => chatGPTReducer.getIsAskingAgent(state));
    return (React.createElement(ClawTaskItem, { type: "SANDBOX_WORK_LIST", defaultOpen: true },
        conversationArrayLength > 0 && !sandboxAibleSkillEnabled && (React.createElement("div", { className: "mb-[10px]" },
            React.createElement(Button, { onClick: () => {
                    dispatch(sendOpenClawMessage("/record_workflow", true));
                }, disabled: isAskingAgent }, "Create Template Setup"))),
        React.createElement("div", { className: "flex flex-col" }, submittedReportExecutionIds.map((executionId, i) => {
            return (React.createElement(SandboxWorkItem, { key: `sandboxWorkItem__${executionId}__${i}`, executionId: executionId, handleSaveWorkflow: handleSaveWorkflow, itemSettings: workItemSettings, updateItemSettings: updateItemSettings }));
        })),
        submittedReportExecutionIds?.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement(CronJobs, { agentC: clawAgent, updateAgentC: (key, data) => {
                        return updateClawAgent({ ...clawAgent, [key]: data });
                    }, useGuardrails: useGuardrails, open: true, useCache: useCache, hideCommunicationChannels: true, disableTests: true, hideTools: true, hideSetupStepDisplay: true, selectWorkflowAgentOptions: workFlowOptions })),
            React.createElement("div", null,
                React.createElement(APIInvocations, { agentC: clawAgent, updateAgentC: (key, data) => {
                        return updateClawAgent({ ...clawAgent, [key]: data });
                    }, useGuardrails: useGuardrails, open: true, useCache: useCache, hideCommunicationChannels: true, disableTests: true, hideTools: true, hideSetupStepDisplay: true, selectWorkflowAgentOptions: workFlowOptions })),
            !showClawSettings && (React.createElement("div", null,
                React.createElement(Button, { onClick: () => updateShowClawSettings(true) }, "Create Template with CronJobs / API Invocations"))),
            showClawSettings && (React.createElement(SaveSettingsOptions, { updateShowSaveSettings: updateShowClawSettings, settings: clawAgent, updateSettings: (val) => updateClawAgent(val), handleSave: handleCreateClawAgent, isSavingWorkflow: isCreatingClawAgent }))))));
};
const ExecutionPromptLabel = ({ executionId, originalPrompt }) => {
    const displayPrompt = originalPrompt ?? `Execution ID: ${executionId}`;
    return (React.createElement(Tooltip, null,
        React.createElement(TooltipTrigger, { asChild: true },
            React.createElement("div", { className: "font-bold truncate" }, displayPrompt)),
        React.createElement(TooltipContent, { className: "max-w-[520px] text-pretty text-left z-[1000]" },
            originalPrompt && React.createElement("div", null, originalPrompt),
            React.createElement("div", { className: "font-bold" }, `Execution ID: ${executionId}`))));
};
const SandboxWorkItem = ({ executionId, handleSaveWorkflow, itemSettings, updateItemSettings }) => {
    const dispatch = useDispatch();
    const [showSaveSettings, updateShowSaveSettings] = useState(false);
    const [isSavingWorkflow, updateIsSavingWorkflow] = useState(false);
    const isFetchingExecutionWorkflow = useSelector((state) => contentGenerationReducer.getIsFetchingExecutionWorkflow(state));
    const executionWorkflow = useSelector((state) => contentGenerationReducer.getExecutionWorkflowById(state, executionId));
    const executionSteps = executionWorkflow?.data?.steps;
    const aibleAnywhereProjects = useSelector((state) => aibleAnywhereReducer.getBase(state));
    const isFetchingExecution = isFetchingExecutionWorkflow?.[executionId];
    const placeHolderId = `new_${executionId}`;
    const placeHolderAgent = aibleAnywhereProjects?.[placeHolderId];
    const originalPrompt = itemSettings?.[executionId]?.originalPrompt;
    const handleSave = async () => {
        updateIsSavingWorkflow(true);
        try {
            handleSaveWorkflow(placeHolderAgent, executionId, itemSettings?.[executionId]);
        }
        catch {
            console.log("Error Saving Workflow:", executionId);
        }
        finally {
            updateIsSavingWorkflow(false);
        }
    };
    useEffect(() => {
        if (!isFetchingExecution && executionWorkflow && aibleAnywhereProjects?.[placeHolderId] === undefined) {
            const agentPlaceholder = {
                id: placeHolderId,
                execution_steps: executionWorkflow?.data?.steps ?? [],
                originalPrompt: itemSettings?.[executionId]?.originalPrompt,
                runToggle: "BACKEND",
                workflowOrigin: "OPENSHELL",
                executionId: executionId,
                isAibleAgent: true,
                isAibleBuilder: true,
                isSavedWork: false
            };
            dispatch(aibleAnywhereReducer.updateAibleAnywhere(placeHolderId, agentPlaceholder));
        }
    }, [executionWorkflow, isFetchingExecution]);
    useEffect(() => {
        // Cleanup placeholder workflow agents from reducer on unmount
        return () => {
            dispatch(aibleAnywhereReducer.deleteIdFromState(placeHolderId));
        };
    }, []);
    if (isFetchingExecution) {
        return (React.createElement("div", { className: styles.sandboxWorkItem },
            React.createElement(ExecutionPromptLabel, { executionId: executionId, originalPrompt: originalPrompt }),
            React.createElement("div", { className: "flex flex-col items-center" },
                React.createElement(LoadingIndicator, { style: { scale: "0.6" } }),
                "Generating workflow from the OpenClaw plan. This may take a little while for more complex tasks.")));
    }
    if (!executionWorkflow) {
        return (React.createElement("div", { className: styles.sandboxWorkItem },
            React.createElement(ExecutionPromptLabel, { executionId: executionId, originalPrompt: originalPrompt }),
            React.createElement(Button, { onClick: () => {
                    dispatch(contentGenerationReducer.fetchExecutionWorkflowPlan(executionId, true));
                }, style: { marginLeft: "10px" } }, "Retry")));
    }
    return (React.createElement("div", { className: styles.sandboxWorkItem },
        React.createElement("div", { className: "flex items-center justify-between gap-4" },
            React.createElement(ExecutionPromptLabel, { executionId: executionId, originalPrompt: originalPrompt }),
            !showSaveSettings && (React.createElement("div", null,
                React.createElement(Button, { onClick: () => updateShowSaveSettings(!showSaveSettings) }, "Save Agent Template")))),
        React.createElement("div", { className: "flex flex-col" },
            React.createElement(SkillsOverview, { agent: { id: placeHolderId, ...placeHolderAgent }, wrapperStyleOverride: { marginTop: "10px", padding: "0px 0px 10px" } })),
        showSaveSettings && (React.createElement(SaveSettingsOptions, { updateShowSaveSettings: updateShowSaveSettings, settings: itemSettings?.[executionId], updateSettings: (val) => updateItemSettings(executionId, val), handleSave: handleSave, isSavingWorkflow: isSavingWorkflow }))));
};
const SaveSettingsOptions = ({ updateShowSaveSettings, settings, updateSettings, handleSave, isSavingWorkflow }) => {
    const updateStateByKey = (key, val) => {
        const stateUpdate = {
            ...settings,
            [key]: val
        };
        updateSettings(stateUpdate);
    };
    return (React.createElement("div", null,
        React.createElement(StandardOptions, { sourceState: settings, updateStateByKey: updateStateByKey }),
        React.createElement(TaggingOptions, { sourceState: settings, updateStateByKey: updateStateByKey }),
        React.createElement("div", { className: styles.saveSettingsOptionsActions },
            React.createElement(Button, { onClick: () => updateShowSaveSettings(false) }, "Cancel"),
            React.createElement(Button, { onClick: () => handleSave(), isLoading: isSavingWorkflow }, "Save Template"))));
};
export const StandardOptions = ({ sourceState, updateStateByKey }) => {
    return (React.createElement("div", { style: { marginTop: "20px", marginBottom: "32px" } },
        React.createElement("div", null,
            React.createElement("div", null, "Agent Name"),
            React.createElement(LineInput, { style: { backgroundColor: "#ffffff" }, value: sourceState?.name, onChange: (val) => updateStateByKey("name", val), inputName: `standardOptionName__${sourceState?.id ?? sourceState?.name}` })),
        React.createElement("div", null,
            React.createElement("div", null, "Agent Description"),
            React.createElement(LineInput, { style: { backgroundColor: "#ffffff" }, value: sourceState?.description, onChange: (val) => updateStateByKey("description", val), inputName: `standardOptionDescription__${sourceState?.id ?? sourceState?.name}` }))));
};
export const TaggingOptions = ({ sourceState, updateStateByKey }) => {
    const templateTags = sourceState?.tags ?? [];
    const tags = useSelector((state) => tagsReducer.getTags(state));
    const tagOptions = Object.keys(tags).map((tagKey) => {
        return {
            ...tags[tagKey],
            value: tags[tagKey].id
        };
    });
    const industryTagOptions = tagOptions?.filter((option) => option?.type === "INDUSTRY");
    const functionTagOptions = tagOptions?.filter((option) => option?.type === "FUNCTION");
    const typeTagOptions = tagOptions?.filter((option) => option?.type === "TYPE");
    const templateIndustryTags = templateTags?.filter((tagId) => tags[tagId]?.type === "INDUSTRY");
    const templateFunctionTags = templateTags?.filter((tagId) => tags[tagId]?.type === "FUNCTION");
    const templateTypeTags = templateTags?.filter((tagId) => tags[tagId]?.type === "TYPE");
    return (React.createElement("div", { style: { marginTop: "20px", marginBottom: "32px" } },
        React.createElement("div", { className: styles.settingSubtitle, style: { marginBottom: "4px" } }, "Template Tags"),
        React.createElement("div", { className: styles.tagSettingContainer },
            React.createElement("div", { className: styles.tagSetting },
                React.createElement("div", { className: styles.tagSettingLabel }, "Industry"),
                React.createElement("div", { className: styles.tagSelectWrapper },
                    React.createElement(Select, { options: industryTagOptions, onChange: (selectedOptions) => {
                            const selectedTags = selectedOptions?.map((option) => option?.value);
                            const retTags = [...selectedTags, ...templateFunctionTags, ...templateTypeTags];
                            updateStateByKey("tags", retTags);
                        }, menuPlacement: "bottom", value: industryTagOptions.filter((option) => sourceState?.tags?.includes(option?.value)), isMulti: true, placeholder: "Select Industries...", style: { width: "100%" }, inputId: `industryTagOptions__${sourceState?.id ?? sourceState?.name}`, components: { Input: CustomSelectInput } }))),
            React.createElement("div", { className: styles.tagSetting },
                React.createElement("div", { className: styles.tagSettingLabel }, "Function"),
                React.createElement("div", { className: styles.tagSelectWrapper },
                    React.createElement(Select, { options: functionTagOptions, onChange: (selectedOptions) => {
                            const selectedTags = selectedOptions?.map((option) => option?.value);
                            const retTags = [...templateIndustryTags, ...selectedTags, ...templateTypeTags];
                            updateStateByKey("tags", retTags);
                        }, menuPlacement: "bottom", value: functionTagOptions.filter((option) => sourceState?.tags?.includes(option?.value)), isMulti: true, placeholder: "Select Functions...", style: { width: "100%" }, inputId: `functionTagOptions__${sourceState?.id ?? sourceState?.name}`, components: { Input: CustomSelectInput } }))),
            React.createElement("div", { className: styles.tagSetting },
                React.createElement("div", { className: styles.tagSettingLabel }, "Type"),
                React.createElement("div", { className: styles.tagSelectWrapper },
                    React.createElement(Select, { options: typeTagOptions, onChange: (selectedOptions) => {
                            const selectedTags = selectedOptions?.map((option) => option?.value);
                            const retTags = [...templateIndustryTags, ...templateFunctionTags, ...selectedTags];
                            updateStateByKey("tags", retTags);
                        }, menuPlacement: "bottom", value: typeTagOptions.filter((option) => sourceState?.tags?.includes(option?.value)), isMulti: true, placeholder: "Select Types...", style: { width: "100%" }, inputId: `typeTagOptions__${sourceState?.id ?? sourceState?.name}`, components: { Input: CustomSelectInput } }))))));
};
const CustomSelectInput = (props) => {
    return React.createElement(components.Input, { ...props, autoComplete: "new-password", name: props?.selectProps?.inputId });
};
export default SandboxWorkList;
